import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';

import { verify } from '../../api';
import { AuthForm } from '../../features/auth';
import logo from '../../images/logo-login.png';
import { translateAuthError } from '../../lib/authError';
import LanguageControl from '../../shared/components/LanguageControl';

function Verify() {
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Login' });

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [stage, setStage] = useState('success');

    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');
    const token = urlParams.get('token');
    const language = urlParams.get('language');

    useEffect(() => {
        if (language && ['english', 'chinese', 'japanese'].includes(language.toLowerCase())) {
            i18n.changeLanguage(language.toLowerCase());
        }
    }, [language, i18n]);

    useEffect(() => {
        if (email && token) {
            setLoading(true);
            verify(email, token)
                .then((data) => {
                    if (data && data.success) {
                        setStage('success');
                    } else {
                        // Legacy path: backend returns 200 with { error } in body.
                        alert(translateAuthError(t, data, 'authError.verificationFailed'));
                    }
                })
                .catch((error) => {
                    // Post-status-code-fix path: 401 (bad token) / 410 (expired)
                    // reject here; the reason lives on response.data.error.
                    // No response → generic fallback (network failure).
                    alert(translateAuthError(t, error, 'authError.verificationFailed'));
                })
                .finally(() => setLoading(false));
        }
    }, [email, token]);

    const redirectToLogin = () => {
        navigate('/login');
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <div className='container-auth relative'>
            <AuthForm
                className={`${stage === 'success' ? 'flex' : 'hidden'} gap-y-7 md:gap-y-0`}
                logoSrc={logo}
                onLogoClick={redirectToLogin}
            >
                <p className='mt-28 mb-8 text-[16px] font-bold text-[#757575] md:text-xl md:text-gray-700'>
                    {t('successAccount')}
                </p>
                <p className='mb-2 text-[10px] text-[#757575] md:text-base md:text-gray-700'>{t('successNote')}</p>
                <div className='w-[360px] md:w-full md:flex md:justify-center'>
                    <Link
                        to='/login'
                        className='flex h-10 w-full items-center justify-center rounded-full bg-blue-600 font-semibold text-white transition hover:bg-blue-700 md:w-[10rem]'
                    >
                        {t('return')}
                    </Link>
                </div>
                <div className='md:absolute md:bottom-2 md:left-1/2 md:-translate-x-1/2 w-full text-center'>
                    <p className='text-sm'>
                        {t('question')}{' '}
                        <a href='mailto:support@gocochain.com' className='text-blue-600 hover:underline'>
                            support@gocochain.com
                        </a>
                    </p>
                </div>
                <div className='block md:hidden'>
                    <LanguageControl />
                </div>
            </AuthForm>
            <p className='absolute bottom-2 left-0 right-0 w-full px-2 text-center text-xs text-brandGray font-roboto'>
                © 2024 Gocochain Co., Ltd. All rights reserved.
            </p>
        </div>
    );
}

export default Verify;
